import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { getAuth, applyActionCode, checkActionCode } from "firebase/auth";
import { useToast } from "../../hooks/useToast";
import { sendRegisterEmail } from "../../services/apiservices/email";
import csmGrayTextLogin from "../../assets/images/csmGrayTextLogin.png";
import { logUserActionBackground, USER_ACTION_EVENTS } from "../../services/userActionLog";
const VerifyEmail = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [statusText, setStatusText] = useState("Verifying your email...");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const queryParameters = new URLSearchParams(window.location.search);
    const oobCode = queryParameters.get("oobCode");
    const emailFromQuery = (queryParameters.get("email") || "")
      .trim()
      .toLowerCase();
    const uidFromQuery = (queryParameters.get("uid") || "").trim();

    logUserActionBackground({
      userId: uidFromQuery || "verify-page-landed",
      eventName: USER_ACTION_EVENTS.VERIFY_EMAIL,
      email: emailFromQuery || "unknown",
    });

    const verifyEmail = async () => {
      if (!oobCode) {
        setLoading(false);
        setStatusText("No verification code found.");
        toast?.({
          title: "Error",
          description: "Verification code is missing.",
          variant: "destructive",
        });
        return;
      }

      try {
        let resolvedEmail = emailFromQuery;
        try {
          const info = await checkActionCode(auth, oobCode);
          const fromAction =
            (typeof info?.data?.email === "string" && info.data.email) ||
            (typeof info?.email === "string" && info.email) ||
            "";
          if (fromAction.trim()) {
            resolvedEmail = fromAction.trim().toLowerCase();
          }
        } catch (checkErr) {
          console.warn("[verify-email] checkActionCode failed:", checkErr?.code || checkErr);
          if (
            checkErr?.code === "auth/invalid-action-code" ||
            checkErr?.code === "auth/expired-action-code"
          ) {
            throw checkErr;
          }
        }

        await applyActionCode(auth, oobCode);

        if (!resolvedEmail && auth?.currentUser?.email) {
          resolvedEmail = auth.currentUser.email.trim().toLowerCase();
        }

        try {
          await new Promise((resolve, reject) => {
            sendRegisterEmail(
              resolvedEmail,
              resolve,
              (err) => reject(err?.error || err || new Error("sendRegisterEmail failed")),
              uidFromQuery || undefined
            );
          });
        } catch (postVerifyError) {
          // applyActionCode already verified the email. Never fail verification UX because
          // of follow-up sync/email tasks.
          console.warn("Post-verification sync failed; continuing login flow:", postVerifyError);
        }

        if (auth?.currentUser) {
          await auth.currentUser.reload();
        }

        setStatusText("Email verified successfully!");
        logUserActionBackground({
          userId: uidFromQuery || auth?.currentUser?.uid || "verified-user",
          eventName: USER_ACTION_EVENTS.VERIFY_EMAIL_SUCCESS,
          email: resolvedEmail,
          displayName: auth?.currentUser?.displayName || "",
        });
        toast?.({
          title: "Success",
          description: "Email verified successfully!",
          variant: "default",
        });

        setTimeout(() => {
          window.location.replace("/thank-you");
        }, 500);

      } catch (error) {
        console.error("[verify-email] verification failure:", error?.code || error);
        setLoading(false);
        let errorMessage = "Verification failed.";
        if (
          error.code === "auth/invalid-action-code" ||
          error.code === "auth/expired-action-code"
        ) {
          errorMessage = "This verification link is invalid or has expired.";
          setStatusText("Link expired or invalid");
        } else if (error.code === "auth/user-disabled") {
          errorMessage = "This account has been disabled.";
          setStatusText("Account disabled");
        } else {
          setStatusText("Verification failed");
        }

        toast?.({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });

        logUserActionBackground({
          userId: uidFromQuery || auth?.currentUser?.uid || "failed-verify-user",
          eventName: USER_ACTION_EVENTS.VERIFY_EMAIL_FAILED,
          email: emailFromQuery || "unknown",
        });
      }
    };

    verifyEmail();
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f0fdf4",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
      }}
    >
      <img
        src={csmGrayTextLogin}
        alt="ProTeams Logo"
        style={{
          height: "80px",
          marginBottom: "30px",
        }}
      />
      <div
        style={{
          background: "white",
          borderRadius: "10px",
          padding: "40px",
          boxShadow: "0 4px 15px rgba(0, 128, 0, 0.2)",
          textAlign: "center",
          maxWidth: "400px",
          width: "100%",
        }}
      >
        {loading && (
          <svg
            width="40"
            height="40"
            viewBox="0 0 50 50"
            style={{ animation: "spin 1s linear infinite", marginBottom: "20px" }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="#16a34a"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="31.4 31.4"
              transform="rotate(-90 25 25)"
            />
          </svg>
        )}
        <h2 style={{ color: "#065f46", fontSize: "20px" }}>{statusText}</h2>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg);}
          100% { transform: rotate(360deg);}
        }
      `}</style>
    </div>
  );
};

export default VerifyEmail;
